import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, ArrowRight } from 'lucide-react';
import { workspaceExtras, NextAction } from '@/lib/workspace-extras';

/**
 * Data-driven "what should I do next?" card. Always tells the user the single
 * highest-value next step (wired to docs / failed-index / approvals / connectors),
 * so the tools stop feeling loosely separated. Never empty — falls back to
 * "explore". This is the connective tissue of the workspace home.
 */
const COPY: Record<NextAction['kind'], { title: string; titleDefault: string; cta: string; ctaDefault: string }> = {
  review_approvals: { title: 'next_action.review_approvals.title', titleDefault: 'You have {{count}} item(s) waiting for sign-off', cta: 'next_action.review_approvals.cta', ctaDefault: 'Review approvals' },
  retry_failed:     { title: 'next_action.retry_failed.title',     titleDefault: '{{count}} document(s) failed to index',         cta: 'next_action.retry_failed.cta',     ctaDefault: 'Review & retry' },
  add_docs:         { title: 'next_action.add_docs.title',         titleDefault: 'Your workspace is empty — add knowledge to begin', cta: 'next_action.add_docs.cta',        ctaDefault: 'Upload a document' },
  fix_connector:    { title: 'next_action.fix_connector.title',    titleDefault: '{{count}} connector(s) need attention',         cta: 'next_action.fix_connector.cta',    ctaDefault: 'Fix connectors' },
  ask_first:        { title: 'next_action.ask_first.title',        titleDefault: 'Your documents are indexed — ask your first question', cta: 'next_action.ask_first.cta',    ctaDefault: 'Ask a question' },
  explore:          { title: 'next_action.explore.title',          titleDefault: 'Ask anything across your workspace — every answer is cited', cta: 'next_action.explore.cta',     ctaDefault: 'Ask a question' },
};

export function NextBestAction({ wsId }: { wsId: string }) {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['next-actions', wsId],
    queryFn: () => workspaceExtras.nextActions(wsId),
    enabled: !!wsId,
  });

  const top = data?.actions?.[0];
  if (!top) return null;
  const copy = COPY[top.kind];

  return (
    <section className="paper-spotlight rounded-2xl p-5 mb-7 flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-start gap-3 min-w-0">
        <span className="shrink-0 h-9 w-9 rounded-xl bg-accent-50 text-accent-700 inline-flex items-center justify-center">
          <Sparkles size={18} />
        </span>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.16em] text-accent-600 font-semibold">
            {t('next_action.eyebrow', { defaultValue: 'Recommended next step' })}
          </div>
          <p className="mt-0.5 text-[14.5px] font-medium text-ink-900">
            {t(copy.title, { defaultValue: copy.titleDefault, count: top.count ?? 0 })}
          </p>
        </div>
      </div>
      <Link
        to={`/app/workspace/${wsId}/${top.to}`}
        className="btn-press inline-flex items-center gap-1.5 px-4 py-2 text-sm shrink-0"
      >
        {t(copy.cta, { defaultValue: copy.ctaDefault })}
        <ArrowRight size={15} />
      </Link>
    </section>
  );
}
